export interface DropdownPosition {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
  placement: 'top' | 'bottom';
}

const DEFAULT_PREFERRED_HEIGHT = 280;
const MIN_SPACE = 120;

export const getDropdownPosition = (
  triggerEl: HTMLElement,
  gap = 4,
  preferredHeight = DEFAULT_PREFERRED_HEIGHT
): DropdownPosition => {
  const rect = triggerEl.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - rect.bottom - gap;
  const spaceAbove = rect.top - gap;

  // Prefer below; flip up when the menu won't fit below and there's more room above.
  const openAbove = spaceBelow < preferredHeight && spaceAbove > spaceBelow;

  if (openAbove) {
    return {
      bottom: viewportHeight - rect.top + gap,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(spaceAbove, MIN_SPACE),
      placement: 'top',
    };
  }

  return {
    top: rect.bottom + gap,
    left: rect.left,
    width: rect.width,
    maxHeight: Math.max(spaceBelow, MIN_SPACE),
    placement: 'bottom',
  };
};

export const subscribeDropdownPosition = (
  triggerEl: HTMLElement,
  onUpdate: (position: DropdownPosition) => void,
  gap = 4,
  preferredHeight = DEFAULT_PREFERRED_HEIGHT
): (() => void) => {
  const update = () => onUpdate(getDropdownPosition(triggerEl, gap, preferredHeight));
  update();
  window.addEventListener('resize', update);
  window.addEventListener('scroll', update, true);
  return () => {
    window.removeEventListener('resize', update);
    window.removeEventListener('scroll', update, true);
  };
};

export const isOutsideAnchors = (
  target: Node,
  anchors: Array<HTMLElement | null | undefined>
): boolean => !anchors.some((anchor) => anchor?.contains(target));
